<template>
  <v-container fluid pa-0>
    <v-layout>
      <v-flex>
        <v-row>
          <v-col cols="12" class="py-0">
            <v-container fluid py-0 pb-1>
              <v-row align="center">
                <div>
                  <h1 class="headline" v-if="showTitle">Itineraries</h1>
                </div>

                <v-col class="py-0">
                  <v-row align="center">
                    <v-col class="py-0">
                      <list-filters
                        :filters="filters"
                        label="Find Itinerary"
                        searchButtonId="findItineraryBtn"
                        :ref-name="refName"
                        :cached-filters="cachedFilters"
                        @searchParamsChanged="searchParamsChanged"
                        :expansionPanel="showFilters"
                        :show-label="showFiltersLabel"
                        :cols="filterCols"
                      ></list-filters>
                    </v-col>
                    <div v-if="!hideAgGrid">
                      <ag-grid-switch :name="refNameColumns"></ag-grid-switch>
                    </div>
                    <div>
                      <list-visible-columns-and-filter
                        :visible-columns="visibleColumns"
                        :selectable-headers="selectableHeaders"
                        v-model="search"
                        @change="onVisibleColumnsChange"
                        :ref-name="refNameColumns"
                      >
                        <v-switch v-model="group" label="Show Group By" />
                      </list-visible-columns-and-filter>
                    </div>
                    <div v-if="!hideCreate">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-btn
                            v-on="on"
                            fab
                            dark
                            color="primary"
                            v-if="
                              $store.getters.hasAccessTo('itineraries:create')
                            "
                            @click="onCreateNew"
                            id="newItineraryBtn"
                          >
                            <v-icon dark>mdi-plus</v-icon>
                          </v-btn>
                        </template>
                        <span>Create New Itinerary</span>
                      </v-tooltip>
                    </div>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
          </v-col>
        </v-row>
        <v-card>
          <v-layout fluid>
            <v-flex xs12>
              <div v-if="showAgGrid && !hideAgGrid" key="ag-grid">
                <ag-grid-vue
                  class="ag-theme-alpine"
                  id="ag-it-list"
                  :columnDefs="agGridColumns"
                  :rowData="items"
                  rowSelection="multiple"
                  :context="agContext"
                  @grid-ready="onAgGridReady"
                  @columnMoved="onAgColumnMoved"
                  @columnResized="onAgColumnResized"
                  @sortChanged="onAgSortChanged"
                  @selection-changed="onAgSelectionChanged"
                  @newColumnsLoaded="onAgNewColumns"
                  suppressDragLeaveHidesColumns
                >
                </ag-grid-vue>

                <list-actions
                  v-if="selected.length > 0 && !hideActions"
                  :selected="selected"
                  list="itineraries"
                  :columns="visibleColumns"
                  :filter="searchParams"
                  style="top: -30px; position: relative"
                >
                  <v-btn fab dark small color="orange" @click="onBatchPay">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon v-on="on">mdi-currency-usd</v-icon>
                      </template>
                      Batch Receipt
                    </v-tooltip>
                  </v-btn>
                  <v-tooltip
                    bottom
                    v-if="$store.getters.hasAccessTo('itineraries:lock')"
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        fab
                        dark
                        small
                        color="red"
                        @click="onLockItineraries(true)"
                      >
                        <v-icon v-on="on">mdi-lock-outline</v-icon>
                      </v-btn>
                    </template>
                    Lock Itineraries
                  </v-tooltip>
                  <v-tooltip
                    bottom
                    v-if="$store.getters.hasAccessTo('itineraries:lock')"
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        fab
                        dark
                        small
                        color="green"
                        @click="onLockItineraries(false)"
                      >
                        <v-icon v-on="on">mdi-lock-open-outline</v-icon>
                      </v-btn>
                    </template>
                    Unlock Itineraries
                  </v-tooltip>
                </list-actions>
              </div>

              <v-data-table
                v-else
                :headers="filteredHeaders"
                :items="editable ? editableItems : items"
                :loading="$apollo.loading"
                :search="search"
                item-key="id"
                class="elevation-1 it-list"
                v-bind="pagingOptions"
                mobile-break-point="1264"
                :show-group-by="group"
                show-select
                v-model="selected"
                fixed-header
                :height="gridHeight"
                @update:options="onListOptionsUpdate"
                key="vtf-grid"
                @item-selected="onItemSelect"
                hide-default-footer
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                @current-items="dataTableReady"
              >
                <template v-slot:item.amount="{ item }">
                  <v-text-field
                    type="number"
                    v-model="item.amount"
                    min="0"
                    class="pt-1 ma-0"
                    style="min-width: 100px"
                    @change="amountChanged(item)"
                  ></v-text-field>
                </template>
                <template v-slot:item.menuActions="{ item }">
                  <list-menu-actions
                    :item="item"
                    @delete="deleteItem"
                    @details="viewItem"
                    @edit="editItem"
                    :title-labeler="(item) => item.reference"
                    :hideRemove="
                      !hasAccessToDelete ||
                      item.hasClientInvoice ||
                      item.hasPurchaseInvoice
                    "
                    details
                  ></list-menu-actions>
                </template>

                <template v-slot:item.clientName="{ item }">
                  <client-label
                    v-model="item.clientName"
                    :client-id="item.client ? item.client.id : ''"
                  ></client-label>
                </template>

                <template v-slot:item.agentType="{ item }">
                  {{ item.agent ? item.agent.agentType : "" }}
                </template>
                <template v-slot:item.agentCode="{ item }">
                  {{ item.agent ? item.agent.companyCode : "" }}
                </template>
                <template v-slot:item.reference="{ item }">
                  <reference-label
                    v-model="item.reference"
                    :itinerary-id="item.id"
                    :newTab="refLabelNewTab"
                  ></reference-label>
                </template>
                <template v-slot:item.sourceItinerary.reference="{ item }">
                  <reference-label
                    v-if="item.sourceItinerary"
                    v-model="item.sourceItinerary.reference"
                    :itinerary-id="item.sourceItinerary.id"
                    :newTab="refLabelNewTab"
                  ></reference-label>
                </template>
                <template v-slot:item.agentName="{ item }">
                  <agent-label
                    v-model="item.agentName"
                    :agent-id="item.agentId"
                  ></agent-label>
                </template>
                <template v-slot:item.bookingStatus="{ item }">
                  <status-label v-model="item.bookingStatus"></status-label>
                </template>
                <template v-slot:item.startDate="{ item }">{{
                  item.startDate | formatDate
                }}</template>
                <template v-slot:item.endDate="{ item }">{{
                  item.endDate | formatDate
                }}</template>
                <template v-slot:item.createdAt="{ item }">{{
                  item.createdAt | formatDate
                }}</template>
                <template v-slot:item.firstSalesInvoiceDate="{ item }">{{
                  item.firstSalesInvoiceDate | formatDate
                }}</template>
                <template v-slot:item.lastPaymentDate="{ item }">{{
                  item.lastPaymentDate | formatDate
                }}</template>
                <template v-slot:item.totalPrice="{ item }">{{
                  item.totalPrice | priceLabeler
                }}</template>
                <template v-slot:item.paid="{ item }">{{
                  item.paid | priceLabeler
                }}</template>
                <template v-slot:item.balance="{ item }">{{
                  item.balance | priceLabeler
                }}</template>
                <template v-slot:item.commission="{ item }">{{
                  item.commission | priceLabeler
                }}</template>
                <template v-slot:item.commissionAdjustment="{ item }">{{
                  item.commissionAdjustment | priceLabeler
                }}</template>
                <template v-slot:item.netCommission="{ item }">{{
                  item.netCommission | priceLabeler
                }}</template>
                <template v-slot:item.supplierCommission="{ item }">{{
                  item.supplierCommission | priceLabeler
                }}</template>
                <template v-slot:item.supplierCommissionReceived="{ item }">{{
                  item.supplierCommissionReceived | priceLabeler
                }}</template>
                <template v-slot:item.supplierCommissionBalance="{ item }">{{
                  item.supplierCommissionBalance | priceLabeler
                }}</template>
                <template v-slot:item.profit.netProfit="{ item }">{{
                  item.profit.netProfit | priceLabeler
                }}</template>
                <template v-slot:item.profit.grossProfit="{ item }">{{
                  item.profit.grossProfit | priceLabeler
                }}</template>
                <template v-slot:item.grossBalance="{ item }">{{
                  item.grossBalance | priceLabeler
                }}</template>
                <template v-slot:item.salesInvoices="{ item }">{{
                  item.salesInvoices.map((inv) => inv.number).join(", ")
                }}</template>
                <template v-slot:item.isLocked="{ item }">
                  <v-simple-checkbox
                    v-model="item.isLocked"
                    disabled
                  ></v-simple-checkbox>
                </template>
                <template v-slot:item.hasServiceGroups="{ item }">
                  <v-simple-checkbox
                    v-model="item.hasServiceGroups"
                    disabled
                  ></v-simple-checkbox>
                </template>
                <template v-slot:item.actions="{ item }">
                  <default-list-actions
                    :item="item"
                    @deleteItem="deleteItem($event)"
                    @viewItem="viewItem($event)"
                    @editItem="editItem($event)"
                    :hideRemove="
                      !hasAccessToDelete ||
                      item.hasClientInvoice ||
                      item.hasPurchaseInvoice
                    "
                    :hideView="false"
                  >
                    <template
                      v-slot:extraButton
                      v-if="item.hasClientInvoice || item.hasPurchaseInvoice"
                    >
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on">mdi-information-variant</v-icon>
                        </template>
                        <span
                          >Remove is not possible as invoice has been created
                          for this itinerary.</span
                        >
                      </v-tooltip>
                    </template>
                  </default-list-actions>
                </template>
                <template v-slot:footer>
                  <list-actions
                    v-if="selected.length > 0 && !hideActions"
                    :selected="selected"
                    list="itineraries"
                    :columns="visibleColumns"
                    :filter="searchParams"
                    @close="selected = []"
                    :hide-export="!hasAccessToExport"
                  >
                    <v-btn
                      fab
                      dark
                      small
                      color="orange"
                      @click="onBatchPay"
                      v-if="
                        !$store.getters.hasAccessTo(
                          'finances:receipts_view_only'
                        )
                      "
                    >
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on">mdi-currency-usd</v-icon>
                        </template>
                        Batch Receipt
                      </v-tooltip>
                    </v-btn>
                    <v-tooltip
                      bottom
                      v-if="$store.getters.hasAccessTo('itineraries:lock')"
                    >
                      <template v-slot:activator="{ on }">
                        <v-btn
                          fab
                          dark
                          small
                          color="red"
                          @click="onLockItineraries(true)"
                        >
                          <v-icon v-on="on">mdi-lock-outline</v-icon>
                        </v-btn>
                      </template>
                      Lock Itineraries
                    </v-tooltip>
                    <v-tooltip
                      bottom
                      v-if="$store.getters.hasAccessTo('itineraries:lock')"
                    >
                      <template v-slot:activator="{ on }">
                        <v-btn
                          fab
                          dark
                          small
                          color="green"
                          @click="onLockItineraries(false)"
                        >
                          <v-icon v-on="on">mdi-lock-open-outline</v-icon>
                        </v-btn>
                      </template>
                      Unlock Itineraries
                    </v-tooltip>
                  </list-actions>
                  <v-row justify="center" v-if="selected.length > 0">
                    <h3 class="py-2 caption">
                      {{ selected.length }} record(s) selected
                    </h3>
                  </v-row>
                </template>
              </v-data-table>
            </v-flex>
          </v-layout>
        </v-card>
        <div v-if="debug">
          <pre>{{ filters }}</pre>
        </div>
        <get-more-results
          :first="first"
          @limitChange="onLimitChange"
          :items="itineraries"
          :totalItems="totalItems"
          :loading="$apollo.loading"
          :loadMoreEnabled="loadMoreEnabled"
          @loadMore="loadMore('itineraries')"
        ></get-more-results>
        <v-row>
          <list-totals v-model="totalsColumns" :items="items"></list-totals>
        </v-row>
        <confirmation-dialog
          :show-dialog="showApproveDialog"
          :max-width="650"
          @onCloseDialog="showApproveDialog = false"
          @onConfirm="onConfirmAction"
        >
          <template slot="message">{{ approveMessage }}</template>
        </confirmation-dialog>
        <batch-create-payments-dialog
          :show="showBatchCreatePayments"
          @close="showBatchCreatePayments = false"
          @complete="onBatchPaymentsComplete"
          :itineraries="selected"
          :columns="filteredHeaders"
        ></batch-create-payments-dialog>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import {
  ALL_ITINERARIES_SEARCH_QUERY,
  ITINERARY_DELETE_MUTATION,
} from "../../../constants/graphql";
import GetMoreResults from "../shared/GetMoreResults";
import DefaultListActions from "../shared/DefaultListActions";
import ListFilters from "../shared/list-filters/ListFilters";
import itinerariesFilters from "./itineraries-filters";
import StatusLabel from "../shared/labels/StatusLabel";
import AgentLabel from "../shared/labels/AgentLabel";
import ReferenceLabel from "../shared/labels/ReferenceLabel";
import ClientLabel from "../shared/labels/ClientLabel";
import { ListMixin } from "../../../mixins/ListMixin";
import AgGridListMixin from "../../../mixins/AgGridListMixin";
import {
  removeGQLTypename,
  sortByDate,
  sortByDateWithFormat,
  sortByReference,
} from "../../../helpers/";
import ListVisibleColumnsAndFilter from "../shared/ListVisibleColumnsAndFilter";
import ListMenuActions from "../shared/ListMenuActions";
import ListActions from "../shared/list-actions/ListActions";

import { AgGridVue } from "ag-grid-vue";
import AgGridSwitch from "../shared/AgGridSwitch";
import gql from "graphql-tag";
import ConfirmationDialog from "../shared/ConfirmationDialog.vue";
import BatchCreatePaymentsDialog from "../finances/payments/BatchCreatePaymentsDialog.vue";
import EventBus from "../../../store/event-bus";
import ListTotals from "../shared/ListTotals.vue";
const LOCK_ITINERARIES = gql`
  mutation lockItineraries($input: [ID!]!) {
    lockItineraries(input: $input)
  }
`;
const UNLOCK_ITINERARIES = gql`
  mutation unlockItineraries($input: [ID!]!) {
    unlockItineraries(input: $input)
  }
`;
export default {
  name: "itinerary-list",

  props: {
    customFilters: {
      type: Object,
    },
    refName: {
      type: String,
      default: () => "it_list",
    },
    refNameColumns: {
      type: String,
      default: () => "itinerary-list-cols",
    },
    customColumns: {
      type: Array,
      default: () => [],
    },
    showTitle: {
      type: Boolean,
      default: () => true,
    },
    cachedFilters: {
      type: Boolean,
      default: () => true,
    },
    showFilters: {
      type: Boolean,
      default: () => false,
    },
    hideAgGrid: {
      type: Boolean,
      default: () => false,
    },
    hideCreate: {
      type: Boolean,
      default: () => false,
    },
    additionalHeaders: {
      type: Array,
      default: () => [],
    },
    editable: {
      type: Boolean,
      default: () => false,
    },
    showFiltersLabel: {
      type: Boolean,
      default: () => false,
    },
    filterCols: {
      type: Number,
      default: () => 3,
    },
    refLabelNewTab: {
      type: Boolean,
      default: () => false,
    },
    fixedHeader: {
      type: Boolean,
      default: () => true,
    },
  },

  mixins: [ListMixin, AgGridListMixin],

  components: {
    GetMoreResults,
    DefaultListActions,
    ListFilters,
    ListVisibleColumnsAndFilter,
    StatusLabel,
    AgentLabel,
    ReferenceLabel,
    ClientLabel,
    ListMenuActions,
    ListActions,
    AgGridVue,
    AgGridSwitch,
    ConfirmationDialog,
    BatchCreatePaymentsDialog,
    ListTotals,
  },

  data() {
    return {
      debug: false,
      selected: [],
      group: false,
      itineraries: {},
      itinerariesFilters: itinerariesFilters,
      visibleColumns: this.$store.getters.localSettings(
        this.refNameColumns,
        this.customColumns.length > 0
          ? [...this.customColumns]
          : [
              "startDate",
              "reference",
              "agentName",
              "totalPrice",
              "bookingStatus",
              "actions",
            ]
      ),
      headers: [
        {
          text: "",
          sortable: false,
          value: "menuActions",
          name: "menuActions",
          width: "65px",
        },
        {
          text: "It Start Date",
          sortable: true,
          value: "startDate",
          name: "startDate",

          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.startDate),
        },
        {
          text: "It End Date",
          sortable: true,
          value: "endDate",
          name: "endDate",

          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),
          maxWidth: "115px",
          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.endDate),
        },
        {
          text: "Reference",
          value: "reference",
          sortable: true,
          name: "reference",

          sort: sortByReference,
          comparator: sortByReference,
          cellRendererFramework: "ReferenceLabel",
        },
        {
          text: "Price",

          value: "totalPrice",
          sortable: true,
          name: "totalPrice",
          groupBy: "currencyCode",
          valueGetter: (payload) => payload.data.totalPrice,
          valueFormatter: (payload) =>
            this.$options.filters.priceLabeler(payload.data.totalPrice),
        },
        {
          text: "Currency",
          value: "currencyCode",
          sortable: true,

          name: "currencyCode",
        },
        {
          text: "Paid",
          value: "paid",
          sortable: true,
          name: "paid",
          groupBy: "currencyCode",

          valueFormatter: (payload) =>
            this.$options.filters.priceLabeler(payload.data.paid),
          valueGetter: (payload) => payload.data.paid,
        },
        {
          text: "Agent Commission",
          value: "commission",
          sortable: true,
          name: "commission",

          valueFormatter: (payload) =>
            this.$options.filters.priceLabeler(payload.data.commission),
          valueGetter: (payload) => payload.data.commission,
        },
        {
          text: "Supplier Commission",
          value: "supplierCommission",
          sortable: true,
          name: "supplierCommission",

          valueFormatter: (payload) =>
            this.$options.filters.priceLabeler(payload.data.supplierCommission),
          valueGetter: (payload) => payload.data.supplierCommission,
        },
        {
          text: "Supplier Commission Received",
          value: "supplierCommissionReceived",
          sortable: true,
          name: "supplierCommissionReceived",

          valueFormatter: (payload) =>
            this.$options.filters.priceLabeler(
              payload.data.supplierCommissionReceived
            ),
          valueGetter: (payload) => payload.data.supplierCommissionReceived,
        },
        {
          text: "Supplier Commission Balance",
          value: "supplierCommissionBalance",
          sortable: true,
          name: "supplierCommissionBalance",

          valueFormatter: (payload) =>
            this.$options.filters.priceLabeler(
              payload.data.supplierCommissionBalance
            ),
          valueGetter: (payload) => payload.data.supplierCommissionBalance,
        },
        {
          text: "Agent Commission Adjustment",
          value: "commissionAdjustment",
          sortable: true,
          name: "commissionAdjustment",
          width: "120 px",

          valueFormatter: (payload) =>
            this.$options.filters.priceLabeler(
              payload.data.commissionAdjustment
            ),
          valueGetter: (payload) => payload.data.commissionAdjustment,
        },
        {
          text: "Agent Net Commission",
          value: "netCommission",
          sortable: true,
          name: "netCommission",
          width: "120 px",

          valueFormatter: (payload) =>
            this.$options.filters.priceLabeler(payload.data.netCommission),
          valueGetter: (payload) => payload.data.netCommission,
        },
        {
          text: "Balance",
          value: "balance",
          sortable: true,
          name: "balance",
          width: "110px",
          groupBy: "currencyCode",

          valueFormatter: (payload) =>
            this.$options.filters.priceLabeler(payload.data.balance),
          valueGetter: (payload) => payload.data.balance,
        },
        {
          text: "Gross Balance",
          value: "grossBalance",
          sortable: true,
          name: "grossBalance",
          width: "110px",

          valueFormatter: (payload) =>
            this.$options.filters.priceLabeler(payload.data.grossBalance),
          valueGetter: (payload) => payload.data.grossBalance,
        },
        {
          text: "Created by",
          value: "createdByName",
          sortable: true,
          name: "createdByName",
        },
        {
          text: "Created at",
          value: "createdAt",
          sortable: true,
          name: "createdAt",
          sort: sortByDate,
          comparator: sortByDateWithFormat(
            this.$store.getters.userConfig.dateFormat
          ),

          width: "115px",

          valueGetter: (payload) =>
            this.$options.filters.formatDate(payload.data.createdAt),
        },
        {
          text: "Booked by",
          value: "bookedByName",
          sortable: true,
          name: "bookedByName",
        },
        {
          text: "Adults",
          value: "adults",
          sortable: true,
          name: "adults",
          width: "100px",

          valueGetter: (payload) => payload.data.pax.adults,
        },

        {
          text: "Children",
          value: "children",
          sortable: true,
          name: "children",

          valueGetter: (payload) => payload.data.pax.children,
        },
        {
          text: "Infants",
          value: "infants",
          sortable: true,
          name: "infants",

          valueGetter: (payload) => payload.data.pax.infants,
        },
        {
          text: "Agent",
          value: "agentName",
          sortable: true,
          name: "agentName",

          cellRendererFramework: "AgentLabel",
        },
        {
          text: "Agent Type",
          value: "agentType",
          sortable: false,
          name: "agentType",
          valueGetter: (payload) => payload.data.agent.agentType,
        },
        {
          text: "Agent Code",
          value: "agentCode",
          sortable: false,
          name: "agentCode",
          valueGetter: (payload) => payload.data.agent.companyCode,
        },
        {
          text: `${
            this.$store.getters.isCPS ? "Itinerary Destination" : "Brand"
          }`,
          value: "brand",
          sortable: true,
          name: "brand",
        },
        {
          text: `${
            this.$store.getters.isCPS ? "Operations Team" : "Lead Source"
          }`,
          value: "leadSource",
          sortable: true,
          name: "leadSource",
        },

        {
          text: "Internal Ref",
          value: "internalReference",
          sortable: true,
          name: "internalReference",
        },
        {
          text: "Internal Status",
          value: "internalStatus",
          sortable: true,
          name: "internalStatus",
        },
        {
          text: "Department",
          value: "department",
          sortable: true,
          name: "department",
        },
        {
          text: "Destination",
          value: "destinationName",
          sortable: true,
          name: "destinationName",
        },
        {
          text: "Client",
          value: "clientName",
          sortable: true,
          name: "clientName",
          cellRendererFramework: "ClientLabel",
        },
        {
          text: "Client Firstname",
          value: "clientFirstName",
          sortable: true,
          name: "clientFirstName",
        },
        {
          text: "Client Surname",
          value: "clientSurname",
          sortable: true,
          name: "clientSurname",
        },
        {
          text: "Client Email",
          value: "clientEmail",
          sortable: true,
          name: "clientEmail",
        },
        {
          text: "Client Telephone",
          value: "client.telephone",
          sortable: true,
          name: "clientTelephone",
          valueGetter: (payload) => payload.data.client.telephone,
        },
        {
          text: "Client Mobile",
          value: "client.mobile",
          sortable: true,
          name: "clientMobile",
          valueGetter: (payload) => payload.data.client.mobile,
        },
        {
          text: "Client Address",
          value: "client.address",
          sortable: true,
          name: "clientAddress",
          valueGetter: (payload) => payload.data.client.address,
        },
        {
          text: "Client Nationality",
          value: "client.nationality",
          sortable: true,
          name: "clientNationality",
          valueGetter: (payload) => payload.data.client.nationality,
        },
        {
          text: "Client Reference",
          value: "clientReference",
          sortable: true,
          name: "clientReference",
        },
        {
          text: "Arrival Hotel",
          value: "arrivalHotel",
          sortable: false,
          name: "arrivalHotel",
        },
        {
          text: "Latest Receipt",
          value: "lastPaymentDate",
          sortable: false,
          name: "lastPaymentDate",
        },
        {
          text: "Invoice Date",
          value: "firstSalesInvoiceDate",
          sortable: false,
          name: "firstSalesInvoiceDate",
        },
        {
          text: "Client Trips",
          value: "client.itiCount",
          sortable: false,
          name: "client.itiCount",
        },
        {
          text: "Net Profit",
          value: "netProfit",
          sortable: true,
          name: "netProfit",
          groupBy: "currencyCode",
          valueFormatter: (payload) =>
            this.$options.filters.priceLabeler(payload.data.profit.netProfit),
          valueGetter: (payload) => payload.data.profit.netProfit,
        },
        {
          text: "Net Markup %",
          value: "profit.markup",
          sortable: true,
          name: "markup",

          valueFormatter: (payload) =>
            this.$options.filters.priceLabeler(payload.data.profit.markup),
          valueGetter: (payload) => payload.data.profit.markup,
        },
        {
          text: "Net Margin %",
          value: "profit.margin",
          sortable: true,
          name: "margin",

          valueFormatter: (payload) =>
            this.$options.filters.priceLabeler(payload.data.profit.margin),
          valueGetter: (payload) => payload.data.profit.margin,
        },
        {
          text: "Gross Profit",
          value: "profit.grossProfit",
          sortable: true,
          name: "grossProfit",

          valueFormatter: (payload) =>
            this.$options.filters.priceLabeler(payload.data.profit.grossProfit),
          valueGetter: (payload) => payload.data.profit.grossProfit,
        },
        {
          text: "Sales Invoices",
          value: "salesInvoices",
          sortable: false,
          name: "salesInvoices",
          valueGetter: (payload) =>
            payload.data.salesInvoices.map((inv) => inv.number).join(", "),
        },
        {
          text: "Status",
          value: "bookingStatus",
          sortable: true,
          name: "bookingStatus",
          width: "120px",

          cellRendererFramework: "StatusLabel",
        },
        {
          text: "Locked",
          value: "isLocked",
          sortable: true,

          name: "isLocked",
        },
        {
          text: "Service Groups",
          value: "hasServiceGroups",
          sortable: false,
          name: "hasServiceGroups",
        },
        {
          text: "Parent Itinerary",
          value: "sourceItinerary.reference",
          sortable: true,
          name: "parentItinerary",
          valueGetter: (payload) =>
            payload.data.sourceItinerary
              ? payload.data.sourceItinerary.reference
              : "",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          groupable: false,
          name: "actions",

          width: "150px",
          cellRendererFramework: "DefaultListActions",
          valueGetter: (payload) => ({
            agGridViewOptions: {
              hideRemove:
                !this.$store.getters.hasAccessTo("itineraries:delete"),
            },
          }),
        },
        ...this.additionalHeaders,
      ],
      showApproveDialog: false,
      approveMessage: "Approve message",
      approveAction: "",
      showBatchCreatePayments: false,
      editableItems: [],
    };
  },

  watch: {
    selected(newVal) {
      this.$emit("select", [...this.selected]);
    },
  },

  computed: {
    selectableHeaders() {
      const hideHeaders = [];
      if (!this.$store.getters.hasAccessTo("itineraries:lead_source")) {
        hideHeaders.push("leadSource");
      }
      return this.headers.filter((h) => hideHeaders.indexOf(h.name) == -1);
    },
    withServiceGroups() {
      return this.visibleColumns.indexOf("hasServiceGroups") != -1;
    },
    withLeadClientTripsCount() {
      return this.visibleColumns.indexOf("leadClientTripsCount") != -1;
    },
    totalsColumns() {
      const cols = ["totalPrice", "paid", "balance", "netProfit"];
      return this.filteredHeaders.filter((h) => cols.indexOf(h.name) != -1);
    },
    refNameData() {
      return this.$store.getters.localSettings(this.refName);
    },
    refNameColumnsData() {
      return this.$store.getters.localSettings(this.refNameColumns);
    },
    hasAccessToDelete() {
      return this.$store.getters.hasAccessTo("itineraries:delete");
    },
    hasAccessToExport() {
      return this.$store.getters.hasAccessTo("itineraries:export");
    },
    items() {
      return this.itineraries && this.itineraries.edges
        ? this.itineraries.edges
            .map(removeGQLTypename)
            .map((item) => ({ ...item, netProfit: item.profit.netProfit }))
        : [];
    },
    filters() {
      const hideFilters = [];
      if (!this.$store.getters.hasAccessTo("itineraries:lead_source")) {
        hideFilters.push("leadSources_eq");
      }
      const list = itinerariesFilters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        if (this.$store.getters.isCPS) {
          if (f.props.filterName == "brands_eq") {
            f.props.label = "Itinerary Destinations";
          }
          if (f.props.filterName == "leadSources_eq") {
            f.props.label = "Operations Team";
          }
        }
        return f;
      });
      if (hideFilters.length > 0) {
        return list.filter(
          (f) => hideFilters.indexOf(f.props.filterName) == -1
        );
      } else {
        return list;
      }
    },
  },

  methods: {
    onItemSelect({ item, value }) {
      if (this.editable) {
        const editedIndex = this.editableItems.findIndex(
          (i) => i.id == item.id
        );
        if (editedIndex > -1) {
          Object.assign(this.editableItems[editedIndex], {
            ...item,
            amount: value
              ? !item.amount
                ? Number(item.balance).toFixed(2)
                : Number(item.amount).toFixed(2)
              : null,
          });
        }
      }
    },
    amountChanged(item) {
      item.amount = Number(item.amount).toFixed(2);
      this.selected = [...this.selected];
      if (this.selected.indexOf(item) < 0) {
        this.selected.push(item);
      }
    },
    onBatchPay() {
      this.showBatchCreatePayments = true;
    },
    onConfirmAction() {
      this.showApproveDialog = false;
      this.lockItineraries();
    },
    onLockItineraries(lock) {
      this.showApproveDialog = true;
      this.approveAction = lock ? "lock" : "unlock";
      this.approveMessage = `This will ${this.approveAction} all selected itineraries`;
    },

    lockItineraries() {
      const mutation =
        this.approveAction == "lock" ? LOCK_ITINERARIES : UNLOCK_ITINERARIES;
      this.$apollo
        .mutate({
          mutation,
          variables: {
            input: this.selected.map((item) => item.id),
          },
        })
        .then(() => {
          if (this.$apollo.queries.itineraries != null) {
            this.$apollo.queries.itineraries.refetch();
          }
        })
        .catch((err) => console.log(err));
    },
    onBatchPaymentsComplete() {
      if (this.$apollo.queries.itineraries != null) {
        this.$apollo.queries.itineraries.refetch();
        this.showBatchCreatePayments = false;
        this.selected = [];
      }
    },
    viewItem(item) {
      this.$router.push("/itinerary/" + item.id + "/details");
    },

    editItem(item) {
      //console.log(item);
      this.$router.push("/itinerary/" + item.id + "/edit");
    },
    searchParamsChanged(params) {
      //this.selected = []
      //keep selection in edit mode
      if (!this.editable) {
        this.selected = [];
      }
      this.loadMoreEnabled = true;
      this.searchParams = { ...params };
    },
    onCreateNew() {
      this.$router.push("/itinerary/new");
    },
    deleteItem(item) {
      this.$apollo
        .mutate({
          mutation: ITINERARY_DELETE_MUTATION,
          variables: {
            id: item.id,
          },
        })
        .then(this.removeGqlObjectHandler, this.removeGqlObjectErrorHandler);
    },
    removeGqlObjectHandler(result) {
      this.$store.dispatch("showAppSnackbar", { msg: "Itinerary removed." });
      if (this.$apollo.queries.itineraries != null) {
        this.$apollo.queries.itineraries.refetch();
      }
    },
    removeGqlObjectErrorHandler(err) {
      const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
      this.$store.dispatch("showAppSnackbar", {
        msg: errorMsg,
        color: "error",
      });
      console.log(err);
    },
  },
  apollo: {
    itineraries: {
      query: ALL_ITINERARIES_SEARCH_QUERY,
      variables() {
        return {
          first: this.first,
          after: this.after,
          filter: {
            ...this.searchParams,
            ...this.customFilters,
          },
          sort: this.sort,
          withServiceGroups: this.withServiceGroups,
          withLeadClientTripsCount: this.withLeadClientTripsCount,
        };
      },
      result({ data }) {
        this.totalItems = data.itineraries.totalCount;
        if (this.editable) {
          this.editableItems = data.itineraries.edges.map(removeGQLTypename);
          //update from previously selected

          if (this.selected.length > 0) {
            for (const item of this.editableItems) {
              const selected = this.selected.find((s) => s.id == item.id);
              if (selected) {
                item.amount = Number(selected.amount).toFixed(2);
              }
            }
          }
          //update selected
          // const newItems = this.editableItems.map((item) => item.id);
          // this.selected = this.selected.filter(
          //   (s) => newItems.indexOf(s.id) > -1
          // );
        }
        this.$emit("loaded");
        ///this.gridApi.hideOverlay();
      },
      fetchPolicy: "network-only",
    },
  },

  created() {
    EventBus.$on("reloadItineraryList", () => {
      if (this.$apollo.queries.itineraries != null) {
        if (this.editable) {
          this.selected = [];
        }
        this.$apollo.queries.itineraries.refetch();
      }
    });
    EventBus.$on("updateItinerarListItems", (cb) => {
      if (this.editable) {
        this.editableItems = this.editableItems.map(cb);
      }
    });
  },
};
</script>

<style>
.it-list table tr td:first-child,
.it-list table tr th:first-child {
  padding: 0 0 0 0px;
}

.it-list table th,
.it-list table td {
  border-right: 1px solid rgba(0, 0, 0, 0.12);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ag-body-viewport-wrapper.ag-layout-normal {
  overflow-x: scroll;
  overflow-y: scroll;
}
#ag-it-list {
  height: calc(100vh - 230px);
}
</style>



// WEBPACK FOOTER //
// src/components/gt/itineraries/ItineraryList.vue